import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TIME_PERIOD_PICKER_OPTIONS } from './time-period-picker.constants';
import {
  TimePeriodSelectOption,
  TimePeriodValue,
} from './time-period-picker.models';
import { TimePeriodPickerService } from './time-period-picker.service';
import { FormControlDynamicComponent } from '@form';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ui-time-period-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormControlDynamicComponent,
    TranslocoDirective,
  ],
  template: `
    <ng-container *transloco="let t">
      <div class="flex flex-row gap-x-3">
        @if (control() && dateControl()) {
          <form-control
            class="min-w-40"
            [control]="control()"
            [label]="t('CENTER.TIME_PICKER.PLACEHOLDER')"
            [type]="'select'"
            [typeInputs]="{ options: options() }"
            (controlChanges)="emitChanges($event)"
          />

          @if (isCustomDate()) {
            <form-control
              [control]="dateControl()"
              [type]="'date'"
              [typeInputs]="{ subType: 'range' }"
            />
          }
        }
      </div>
    </ng-container>
  `,
})
export class TimePeriodPickerComponent implements OnDestroy, OnInit {
  public controlId = input.required<string>();
  public dateControlId = input.required<string>();
  public onChanges = output<TimePeriodValue>();

  protected readonly DEFAULT_TIME_PERIOD: TimePeriodValue = 'last24h';

  protected readonly timePeriodPickerService = inject(TimePeriodPickerService);
  protected readonly translocoService = inject(TranslocoService);
  protected readonly destroyRef = inject(DestroyRef);

  protected readonly DEFAULT_OPTIONS = signal(TIME_PERIOD_PICKER_OPTIONS);
  protected readonly controlValue = signal<TimePeriodValue>(
    this.DEFAULT_TIME_PERIOD,
  );
  protected readonly localStorageKey = computed(() => this.controlId());
  protected readonly localStorageDatesRangeKey = computed(() =>
    this.dateControlId(),
  );
  protected readonly isCustomDate = signal<boolean>(
    this.controlValue() === 'customDate',
  );

  protected readonly form = computed(() => {
    return new FormGroup({
      [this.controlId()]: new FormControl<string>(''),
      [this.dateControlId()]: new FormControl<any>(null),
    });
  });

  protected readonly control = computed(
    () => this.form().get(this.controlId()) as FormControl<TimePeriodValue>,
  );

  protected readonly dateControl = computed(
    () => this.form().get(this.dateControlId()) as FormControl<any>,
  );

  protected readonly options = signal<ReadonlyArray<TimePeriodSelectOption>>(
    [],
  );

  effect1 = effect(() => {
    const ctrl = this.control();
    const datesCtrl = this.dateControl();

    if (ctrl && datesCtrl) {
      this.timePeriodPickerService.initialize(
        this.localStorageKey(),
        this.localStorageDatesRangeKey(),
        ctrl,
        datesCtrl,
      );

      this.handleIsCustomDate();
    }
  });

  constructor() {
    this.setLocalizeOptions(this.DEFAULT_OPTIONS());
    localStorage.setItem('centerPeriod', 'last24h');
    const safeDates: Date[] = this.timePeriodPickerService.DEFAULT_RANGE_DATES;
    this.timePeriodPickerService.currentDatesRange.set(safeDates);
    this.timePeriodPickerService.saveRangeDatesLocalStorage(safeDates);
    this.timePeriodPickerService.setPeriod(this.DEFAULT_TIME_PERIOD);
  }

  ngOnInit(): void {
    localStorage.setItem('centerPeriod', 'last24h');
    const safeDates: Date[] = this.timePeriodPickerService.DEFAULT_RANGE_DATES;
    this.timePeriodPickerService.currentDatesRange.set(safeDates);
    this.timePeriodPickerService.saveRangeDatesLocalStorage(safeDates);
    this.timePeriodPickerService.setPeriod(this.DEFAULT_TIME_PERIOD);
  }

  ngOnDestroy(): void {
    localStorage.setItem('centerPeriod', 'last24h');
    const safeDates: Date[] = this.timePeriodPickerService.DEFAULT_RANGE_DATES;
    this.timePeriodPickerService.currentDatesRange.set(safeDates);
    this.timePeriodPickerService.saveRangeDatesLocalStorage(safeDates);
    this.timePeriodPickerService.setPeriod(this.DEFAULT_TIME_PERIOD);
  }

  emitChanges(value: TimePeriodValue) {
    this.onChanges.emit(value);
  }

  handleIsCustomDate() {
    const isCustomDate = this.control().value === 'customDate';
    this.isCustomDate.set(isCustomDate);

    this.control().valueChanges.subscribe((value) => {
      this.controlValue.set(value);
      const isCustomDate = this.controlValue() === 'customDate';
      this.isCustomDate.set(isCustomDate);
    });
  }

  setLocalizeOptions(options: ReadonlyArray<TimePeriodSelectOption>) {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const translatedOptions = options.map((option) => ({
          ...option,
          label: this.translocoService.translate(option.label),
        }));
        this.options.set(translatedOptions);
      });
  }
}
